import {computed, Directive, effect, inject, input, output, signal} from '@angular/core';
import {AbstractControlOptions, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {DirectusService} from '@frontend/directus';
import {AbstractCurrencyDirective} from './abstract-currency-directive';

export type FormControlsConfig<T extends Record<string, any>> = {
  [K in keyof T]: [
    T[K] | null | {value: T[K] | null; disabled: boolean}, // Updated to allow state object
    (AbstractControlOptions | Validators | any)?,
  ];
};

@Directive()
export abstract class AbstractFormDirective<
  T extends Record<string, any>,
> extends AbstractCurrencyDirective {
  // ---- injects -------
  protected readonly fb = inject(FormBuilder);
  protected readonly directusService = inject(DirectusService);

  // ---- input/output -------
  inputObject = input.required<T>();
  readonly isSaving = input(false);
  readonly isReadOnly = input<boolean>(false);
  readonly forceInitialSave = input(false);
  readonly submitOutput = output<T>();

  // ---- the Form -------
  readonly theForm: FormGroup;
  protected readonly formValueSignal = signal<Partial<T>>({}); // start empty

  // eslint-disable-next-line @angular-eslint/prefer-inject
  protected constructor(controlConfig: FormControlsConfig<T>) {
    super();
    this.theForm = this.fb.group(controlConfig);

    this.theForm.valueChanges.subscribe((value) => {
      this.formValueSignal.set(value as T);
    });

    effect(() => {
      const input = this.inputObject();
      if (!input) return;

      // If the input object is empty, reset the form to its initial state
      if (Object.keys(input).length === 0) {
        this.theForm.reset();
        this.formValueSignal.set(this.theForm.getRawValue() as T);
        return;
      }

      // take an input object and produce a patch containing only the keys that exist in the form.
      const patch: Partial<T> = {};
      Object.keys(input).forEach((key) => {
        if (this.theForm.contains(key)) {
          patch[key as keyof T] = input[key as keyof T];
        }
      });

      this.theForm.patchValue(patch, {emitEvent: false});
      this.formValueSignal.set(this.theForm.getRawValue() as T);
    });
  }

  // ---- form actions -------
  readonly hasChanges = computed(() => {
    if (this.forceInitialSave()) return true;

    const original = this.inputObject();
    const current = this.formValueSignal();

    if (!original || !current) return false;

    return Object.keys(original).some((key) => {
      // Check if this key exists in the form controls
      if (!this.theForm.contains(key)) return false;

      const control = this.theForm.get(key);
      if (control?.disabled) return false;

      const originalValue = (original as any)[key];
      const currentValue = (current as any)[key];

      return this.isFieldValueChanged(key, originalValue, currentValue);
    });
  });

  protected isFieldValueChanged(key: string, originalValue: any, currentValue: any): boolean {
    return !this.deepEqual(originalValue, currentValue);
  }

  onSubmit(): void {
    const payload = this.theForm.getRawValue() as T;
    this.submitOutput.emit(payload);
  }

  protected deepEqual(a: any, b: any): boolean {
    if (a === b) return true;

    // Handle Dates or Date-like strings
    const dateA = this.toDate(a);
    const dateB = this.toDate(b);
    if (dateA && dateB) {
      return dateA.getTime() === dateB.getTime();
    }

    if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) return false;

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;

    return keysA.every((key) => this.deepEqual(a[key], b[key]));
  }

  private toDate(value: any): Date | null {
    if (value instanceof Date) return value;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  }
}
